import { UdeFilterDisplay } from 'app/views/boards/panels/ude/filters/UdeFilterDisplay';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, FlexColumn, Icon, Tab, Tabs, Text } from 'core/components';
import { observer } from 'mobx-react';
import { Classes } from '@blueprintjs/core';
import QueryModel from 'app/stores/ude/QueryModel';
import QueryRequest from 'app/stores/ude/QueryRequest';
import { getDimensions, getMeasurementDimensionLabel, getMeasurementMetricLabel } from '@kentik/ui-shared/query/query';
import { uniqueId } from 'lodash';
import { useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import styled from 'styled-components';
import { formatDimensionValue, renderMetricValue } from 'app/views/boards/panels/ude/legendUtils';
import UdeEventsTable from 'app/views/boards/panels/ude/UdeEventsTable';
import { action } from 'mobx';
import type { Measurement } from '@kentik/ui-shared/query/query.types';
import { Popover2 } from 'core/components/Popover';
import UdeTable, { getUdeTableHeight } from './UdeTable';
import SeriesColorLine from './SeriesColorLine';
import getRollupResults, { RollupResult } from './util/getRollupResults';
import UdeRowActionsMenu from './UdeRowActionsMenu';
import type { AddRowFilters } from './rowFilters';

const LegendTabs = styled(Tabs)`
  .${Classes.TAB_PANEL} {
    margin-top: 0;
    padding-bottom: 0;
  }
`;

interface UdeLegendProps {
  queryModel: QueryModel;
  position?: 'bottom' | 'right' | 'left';
  /** When set, overrides the default max-width for a side-positioned legend (default '40%'). */
  maxWidth?: number | string;
  onAddRowFilters?: AddRowFilters;
}

/** Condensed legend item — the color line indicator signals whether the series is on the chart. */
const CondensedItem = styled(Flex)<{ $isCharted: boolean; $isRight: boolean }>`
  cursor: ${({ $isCharted }) => ($isCharted ? 'pointer' : 'default')};
  font-size: 12px;
  white-space: nowrap;
  border-bottom: ${({ $isRight, theme }) =>
    $isRight ? `1px solid ${theme.borderColors.divider || 'rgba(128, 128, 128, 0.25)'}` : 'none'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme, $isCharted }) => ($isCharted ? theme.backgrounds.tableHeaderHover : 'none')};
    border-radius: 3px;
  }
`;

interface CondensedLegendProps {
  queryRequest: QueryRequest;
  position: 'bottom' | 'right' | 'left';
  dimensions: ReturnType<typeof getDimensions>;
  onRowClick: (rowId: string, rowIndex: number, event: React.MouseEvent) => void;
  onRowMouseEnter: (rowId: string) => void;
  onRowMouseLeave: () => void;
  rollups: RollupResult[];
  showRollups: boolean;
  measurement: Measurement;
  onAddRowFilters?: AddRowFilters;
}

const CondensedLegend = observer(
  ({
    queryRequest,
    position,
    dimensions,
    onRowClick,
    onRowMouseEnter,
    onRowMouseLeave,
    rollups,
    showRollups,
    measurement,
    onAddRowFilters
  }: CondensedLegendProps) => {
    const { queryModel, results } = queryRequest;
    const isRightPos = position === 'right' || position === 'left';

    return (
      <Flex
        flexWrap={isRightPos ? 'nowrap' : 'wrap'}
        flexDirection={isRightPos ? 'column' : 'row'}
        gap={isRightPos ? '0px' : '4px 12px'}
        p={1}
      >
        {results.rows.map((row, idx) => {
          const isVisible = queryModel.isSeriesVisible(row.id);
          const isCharted = queryRequest.isRowCharted(row.id);
          const seriesColor = isCharted ? queryModel.getSeriesColor(row.id) : undefined;
          const isSelected = isVisible && isCharted;
          const label = dimensions
            .map((dim) => formatDimensionValue(dim.name, row.dimensions[dim.name]))
            .join(' \u2013 ');

          return (
            <CondensedItem
              key={row.id}
              $isCharted={isCharted}
              $isRight={isRightPos}
              alignItems="center"
              gap="4px"
              px="6px"
              py="4px"
              onClick={isCharted ? (e: React.MouseEvent) => onRowClick(row.id, idx, e) : undefined}
              onMouseEnter={isCharted ? () => onRowMouseEnter(row.id) : undefined}
              onMouseLeave={isCharted ? onRowMouseLeave : undefined}
            >
              <SeriesColorLine color={seriesColor} isSelected={isSelected} />
              <span
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260, whiteSpace: 'nowrap' }}
                title={label}
              >
                {label}
              </span>
              {showRollups && rollups.length > 0 && (
                <span
                  style={{
                    opacity: 0.7,
                    fontSize: 11,
                    marginLeft: 'auto',
                    paddingLeft: 8,
                    whiteSpace: 'nowrap',
                    textAlign: 'right'
                  }}
                >
                  {rollups.map((rollup, rIdx) => (
                    <span key={rollup.key} style={{ marginLeft: rIdx > 0 ? 6 : 0 }}>
                      {renderMetricValue(rollup.metric, rollup.values[idx], {
                        metricInfo: rollup.metricInfo,
                        forcePrefix: rollup.prefix
                      })}
                    </span>
                  ))}
                </span>
              )}
              {onAddRowFilters && (
                <span onClick={(e) => e.stopPropagation()} role="presentation">
                  <UdeRowActionsMenu
                    row={row.get()}
                    query={queryModel.query}
                    measurement={measurement}
                    onAddFilters={onAddRowFilters}
                  />
                </span>
              )}
            </CondensedItem>
          );
        })}
      </Flex>
    );
  }
);

const UdeLegendTabContent = observer(
  ({
    queryRequest,
    isEvents = false,
    onAddRowFilters
  }: {
    queryRequest: QueryRequest;
    isEvents?: boolean;
    onAddRowFilters: AddRowFilters | undefined;
  }) => {
    const { queryModel, query, results, settings } = queryRequest;
    const isTableOnly = settings.chart.type === 'table';
    const position = isTableOnly ? 'bottom' : (queryRequest.settings?.chart?.legend?.position ?? 'right');
    const mode = isTableOnly ? 'table' : (queryRequest.settings?.chart?.legend?.mode ?? 'condensed');
    const isCondensed = mode === 'condensed';
    const dimensions = getDimensions(query);
    const { measurement } = useDictionaryMeasurement(query.measurement);
    const lastClickedIndexRef = useRef<number | null>(null);
    const showRollups = isTableOnly || (queryModel.settings?.chart?.legend?.showRollups ?? true);

    // Reset stale shift-click anchor when results change (rows may have shifted or shrunk)
    useEffect(() => {
      lastClickedIndexRef.current = null;
    }, [results]);

    const rollups = useMemo(
      () => {
        if (!measurement) {
          return [];
        }
        return getRollupResults(queryRequest, measurement);
      },
      // rollups depend on query and results
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [queryRequest, query, results.lastUpdated, measurement]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleRowClick = useCallback(
      action((rowId: string, rowIndex: number, event: React.MouseEvent) => {
        // If the click originated from an <a> tag (e.g. SynthAgent/SynthTest links), let it navigate
        const target = event.target as HTMLElement;
        if (target.closest('a')) {
          return;
        }

        const { rows } = results;

        if (event.shiftKey && lastClickedIndexRef.current !== null) {
          document.getSelection()?.empty(); // Clear text selection caused by shift-click
          // Shift+click: toggle range from last-clicked to current
          const start = Math.min(lastClickedIndexRef.current, rowIndex);
          const end = Math.max(lastClickedIndexRef.current, rowIndex);
          const targetVisible = !queryModel.isSeriesVisible(rowId);
          for (let i = start; i <= end; i++) {
            queryModel.setSeriesVisibility(rows[i].id, targetVisible);
          }
        } else if (event.metaKey || event.ctrlKey) {
          // Cmd+click (Mac) / Ctrl+click (Win): toggle single without affecting others
          queryModel.toggleSeriesVisibility(rowId);
        } else {
          // Plain click: "solo" behavior — if this is the only visible row, show all; otherwise solo it
          const visibleCount = queryModel.chartedRowIds.filter((id) => queryModel.isSeriesVisible(id)).length;
          const isOnlyVisible = visibleCount === 1 && queryModel.isSeriesVisible(rowId);

          if (isOnlyVisible) {
            queryModel.showAllSeries();
          } else {
            queryModel.hideAllSeries();
            queryModel.setSeriesVisibility(rowId, true);
          }
        }

        lastClickedIndexRef.current = rowIndex;
      }),
      [queryModel, results]
    );

    const handleRowMouseEnter = useCallback((rowId: string) => queryModel.setHoveredSeries(rowId), [queryModel]);
    const handleRowMouseLeave = useCallback(() => queryModel.setHoveredSeries(null), [queryModel]);

    if (!measurement || settings.chart.type === 'timelineView') {
      return null;
    }

    if (isEvents) {
      return <UdeEventsTable queryRequest={queryRequest} />;
    }

    if (isCondensed) {
      return (
        <CondensedLegend
          queryRequest={queryRequest}
          position={position}
          dimensions={dimensions}
          onRowClick={handleRowClick}
          onRowMouseEnter={handleRowMouseEnter}
          onRowMouseLeave={handleRowMouseLeave}
          rollups={rollups}
          showRollups={showRollups}
          measurement={measurement}
          onAddRowFilters={onAddRowFilters}
        />
      );
    }

    return (
      <UdeTable
        queryRequest={queryRequest}
        measurement={measurement}
        rollups={rollups}
        onRowClick={handleRowClick}
        onRowMouseEnter={handleRowMouseEnter}
        onRowMouseLeave={handleRowMouseLeave}
        onAddRowFilters={onAddRowFilters}
      />
    );
  }
);

function UdeLegend({ queryModel, position = 'bottom', maxWidth: maxWidthOverride, onAddRowFilters }: UdeLegendProps) {
  const tabsId = useRef(uniqueId('ude-legend-tabs-'));
  const { mainQueryRequest, query, settings } = queryModel;
  const isRight = position === 'right' || position === 'left';
  const isTableOnly = settings.chart.type === 'table';
  const mode = isTableOnly ? 'table' : (queryModel.settings?.chart?.legend?.mode ?? 'condensed');
  const isCondensed = mode === 'condensed';
  const { measurement } = useDictionaryMeasurement(query.measurement);
  const showRollups = isTableOnly || (queryModel.settings?.chart?.legend?.showRollups ?? true);
  const showMirrorResults = !!settings.chart.yAxis?.mirrorMode && queryModel.mirrorQueryRequest.hasRun;
  const showEventsView =
    (measurement?.measurement?.startsWith('/events/') ?? false) &&
    (queryModel.settings?.chart?.legend?.showEventsView ?? true);
  const [selectedTabId, setSelectedTabId] = useState<string>('chart-legend');

  function getOverflow(): 'hidden' | 'auto' | undefined {
    if (showEventsView) {
      return 'hidden';
    }
    if (isCondensed) {
      return 'auto';
    }
    return undefined;
  }

  function getContentHeight(rowCount: number): number {
    return isCondensed ? 30 * (rowCount + 1) : getUdeTableHeight(rowCount, { showTotalRow: showRollups });
  }

  function getMaxHeight(): string {
    if (isRight || isTableOnly) {
      return '100%';
    }
    if (showEventsView) {
      return '40%';
    }
    return `min(40%, ${getContentHeight(mainQueryRequest.results.size)}px)`;
  }

  function getFlex(): string | number {
    if (showEventsView && selectedTabId === 'chart-legend') {
      return '0 1 auto';
    }
    return isCondensed ? '0 1 auto' : 1;
  }

  if (!measurement || settings.chart.type === 'timelineView') {
    return null;
  }

  const tabs: { id: string; height: number; title: string; info?: React.ReactElement; content: React.ReactElement }[] =
    [];

  tabs.push({
    id: 'chart-legend',
    title: showMirrorResults ? 'Original Query' : 'Legend',
    height: getContentHeight(mainQueryRequest.results.size),
    content: <UdeLegendTabContent queryRequest={mainQueryRequest} onAddRowFilters={onAddRowFilters} />
  });

  if (showMirrorResults) {
    const { mirrorQueryRequest } = queryModel;
    const mirrorQuery = mirrorQueryRequest.query;
    tabs.push({
      id: 'mirror-legend',
      title: 'Opposite Query',
      height: getContentHeight(mirrorQueryRequest.results.size),
      info: (
        <Box maxWidth={500} p={2}>
          <Box>
            Bi-directional charting automatically swaps your settings with their opposites (e.g. Source vs. Destination)
            to compare data on the same time series.
          </Box>
          <FlexColumn gap="6px" mt={1}>
            <Box fontWeight="bold">Opposite query summary</Box>
            {mirrorQuery.metrics.length > 0 && (
              <Box>
                <Text as="div" fontWeight="medium" small muted>
                  Units
                </Text>
                {mirrorQuery.metrics.map((m) => getMeasurementMetricLabel(measurement, m.name)).join(', ')}
              </Box>
            )}
            {mirrorQuery.dimensions.length > 0 && (
              <Box>
                <Text as="div" fontWeight="medium" small muted>
                  Dimensions
                </Text>
                {mirrorQuery.dimensions.map((d) => getMeasurementDimensionLabel(measurement, d.name)).join(', ')}
              </Box>
            )}
            {!!mirrorQuery.filters.dimensions?.filterGroups?.length && (
              <Box>
                <Text as="div" fontWeight="medium" small muted>
                  Filters
                </Text>
                <UdeFilterDisplay
                  filters={mirrorQuery.filters.dimensions}
                  type="dimensions"
                  measurementName={query.measurement}
                />
              </Box>
            )}
          </FlexColumn>
        </Box>
      ),
      content: <UdeLegendTabContent queryRequest={queryModel.mirrorQueryRequest} onAddRowFilters={onAddRowFilters} />
    });
  }

  if (showEventsView) {
    tabs.push({
      id: 'events-view',
      title: showMirrorResults ? 'Event (Original Query)' : 'Events',
      height: getContentHeight(mainQueryRequest.rawRows.length),
      content: <UdeLegendTabContent queryRequest={mainQueryRequest} onAddRowFilters={onAddRowFilters} isEvents />
    });

    if (showMirrorResults) {
      tabs.push({
        id: 'mirror-events-view',
        title: 'Events (Opposite Query)',
        height: getContentHeight(queryModel.mirrorQueryRequest.rawRows.length),
        content: (
          <UdeLegendTabContent
            queryRequest={queryModel.mirrorQueryRequest}
            onAddRowFilters={onAddRowFilters}
            isEvents
          />
        )
      });
    }
  }

  return (
    <Flex
      flexDirection="column"
      className="bp4-elevation-0"
      bg="cardBackground"
      flex={getFlex()}
      width={isRight ? 'fit-content' : undefined}
      maxHeight={getMaxHeight()}
      maxWidth={isRight ? (maxWidthOverride ?? '40%') : '100%'}
      minWidth={isRight ? '200px' : undefined}
      overflow={getOverflow()}
    >
      {tabs.length === 1 && tabs[0].content}
      {tabs.length > 1 && (
        <LegendTabs
          flexed
          bordered
          overflowed
          outerFlexStyles={{ margin: '-1px', position: 'relative', minHeight: 0 }}
          id={tabsId.current}
          selectedTabId={selectedTabId}
          onChange={(newTabId: string) => setSelectedTabId(newTabId)}
        >
          {tabs.map(({ id, title, height, content, info }) => (
            <Tab
              key={id}
              id={id}
              title={
                <Flex gap={1} alignItems="center" px={2} py={1}>
                  <Text fontWeight="bold">{title}</Text>
                  {info && (
                    <Popover2 content={info} interactionKind="hover" position="bottom">
                      <Icon icon="info-sign" />
                    </Popover2>
                  )}
                </Flex>
              }
              panel={
                <Flex flexDirection="column" height={height} minHeight="100%" maxHeight="100%" flex={1} p={0}>
                  {content}
                </Flex>
              }
            />
          ))}
        </LegendTabs>
      )}
    </Flex>
  );
}

export default observer(UdeLegend);
